import { EVA_RESEARCH_NAME } from "constants/app.constants";
import {
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_SEARCH_FOLDER_NAME,
  SYNC_DB_FOLDER_NAME,
  WORKING_FORECAST_FOLDER_NAME
} from "constants/settings.constants";

import { ChartSeries } from "models/model";

export enum ForecastCompareLineStyle {
  Dashed = "dashed",
  Dotted = "dotted"
}

export interface ForecastCompareFolder {
  name: string;
  lineStyle: ForecastCompareLineStyle;
}

/**
 * Display names for the special forecast sources **as the chart service spells them on the
 * wire** — the token it puts inside the series label, which `forecastSource` carries verbatim
 * (`ForecastExtensions.cs`: MCD is already branded "EVA Research", Novi is "NOVI", Working is
 * "WORKING").
 *
 * Deliberately not `getForecastFolderDisplayName` from qualifyForecastProperty, despite the
 * overlap: that map keys on the tokens a *property* stores ("shaleProfileDB",
 * "forecastWorking"), which the service never sends here, so it would miss both of these and
 * leave the raw "NOVI"/"WORKING" on screen. Two namespaces for the same four sources, and only
 * this one describes the response. Extending the shared map with wire tokens instead would
 * reach its other consumer (the filter's folder-name resolver), which reads property tokens.
 *
 * McDaniel is listed under its raw folder spelling too: the service has two label paths that
 * disagree on it — the special-source label brands it, while the secondary-folder label echoes
 * back whatever the client sent. (EVA-113)
 */
const WIRE_SOURCE_DISPLAY_NAMES: Record<string, string> = {
  [MCDANIEL_SEARCH_FOLDER_NAME]: EVA_RESEARCH_NAME,
  NOVI: NOVI_SEARCH_FOLDER_NAME,
  WORKING: WORKING_FORECAST_FOLDER_NAME
};

/** How the service joins a label that resolved to more than one source. */
const COMPOSITE_SOURCE_SEPARATOR = " + ";

/** The opening the service appends a forecast series' source in (`GetForecastLabel`). */
const FORECAST_LABEL_MARKER = " Forecast (";

/**
 * The source a forecast series names, preferring the series label over the `forecastSource`
 * field.
 *
 * They are the same string in every case but one. `forecastSource` is not carried as its own
 * field — the service re-parses it out of the label with `LastIndexOf('(')`
 * (`EVA.Chart/Util/Utils.cs`), so a folder whose name contains parentheses comes back truncated
 * to its own tail: "2026 Budget (Draft)" arrives as "Draft)". Anchoring on the marker the label
 * was built with instead recovers the whole name, and takes the LAST marker so a group title
 * that happens to contain one cannot capture the wrong span.
 *
 * Folder names aren't charset-validated anywhere in the UI, so this is reachable, and the
 * legend used to show these names intact when it read them from settings. The robust fix is
 * still service-side — carry the source as its own field — at which point this falls back to it
 * for free. (EVA-113)
 */
export function getSeriesForecastSource(series: ChartSeries): string {
  const label = series?.label ?? "";
  const markerIndex = label.lastIndexOf(FORECAST_LABEL_MARKER);
  if (markerIndex !== -1 && label.endsWith(")")) {
    const source = label.slice(markerIndex + FORECAST_LABEL_MARKER.length, -1);
    if (source) {
      return source;
    }
  }
  return series?.forecastSource ?? "";
}

/**
 * The display name for a forecast source as the chart service reports it on the series.
 *
 * "Sync DB" is not a name at all — it is the placeholder the synced-database forecast is
 * labelled with — so it resolves to the schema that was actually synced, which is what the user
 * recognises. Everything else is either a special source (renamed above) or a user folder, whose
 * name is already what the user chose. (EVA-113)
 */
export function getForecastSourceDisplayName(
  source: string,
  syncedSchema?: Maybe<string>
): string {
  // Falls through to the placeholder when nothing knows the schema — a wrong-looking "Sync DB"
  // is still better than a blank line indicator.
  if (source === SYNC_DB_FOLDER_NAME) {
    return syncedSchema?.trim() || SYNC_DB_FOLDER_NAME;
  }

  // A label that resolved to several sources ("WORKING + EVA Research") needs each part
  // renamed, not the whole string looked up. Left alone when there is nothing to split, so a
  // user folder is never reshaped by the round trip.
  if (!source.includes(COMPOSITE_SOURCE_SEPARATOR)) {
    return WIRE_SOURCE_DISPLAY_NAMES[source] ?? source;
  }
  return source
    .split(COMPOSITE_SOURCE_SEPARATOR)
    .map((part) => WIRE_SOURCE_DISPLAY_NAMES[part] ?? part)
    .join(COMPOSITE_SOURCE_SEPARATOR);
}

/**
 * The two forecast sources a Forecast Compare chart is drawing, each paired with the line style
 * the chart drew it with.
 *
 * Read off the response rather than off the user's forecast settings, because only the response
 * knows which source ended up primary. Sync mode is the case that made the difference: the chart
 * service makes the synced-database forecast primary (dashed) and demotes the selected folder to
 * the comparison (dotted), which is the opposite of the settings-derived order that always put
 * EVA Research first. The legend named the dashed line "EVA Research" while the chart drew EVA
 * Research dotted, so hovering the line the legend called the synced database produced a tooltip
 * reading "EVA Research" and the two series were indistinguishable. (EVA-113)
 *
 * Each entry takes its style from the bucket its series sat in — `isComparisonForecast`, the
 * same flag the translators style the line off — never from its position in the output. Position
 * would re-introduce the same class of mismatch whenever a bucket comes back empty: a response
 * whose primary series carries no source at all would hand the comparison the first slot, and
 * with it the dashed style the chart never drew.
 */
export function getForecastCompareFolders(
  series: Maybe<ChartSeries[]>,
  syncedSchema?: Maybe<string>
): ForecastCompareFolder[] {
  if (!series?.length) {
    return [];
  }

  const forecastSeries = series
    .filter((item) => item?.isForecast)
    .map((item) => ({ item, source: getSeriesForecastSource(item) }))
    .filter(({ source }) => Boolean(source));

  const folders: ForecastCompareFolder[] = [];
  const seen = new Set<string>();
  const addSource = (source: string, lineStyle: ForecastCompareLineStyle) => {
    const name = getForecastSourceDisplayName(source, syncedSchema);
    // Deduped on the display name, not the raw source: a chart with many groups repeats both
    // sources once per group, and the service's two spellings of McDaniel would otherwise take
    // a slot each and put "EVA Research" on screen twice — the very thing EVA-113 reported.
    if (seen.has(name)) {
      return;
    }
    seen.add(name);
    folders.push({ name, lineStyle });
  };

  // Primaries first, so the dashed entry is the one the chart actually dashed. A source in both
  // buckets keeps the primary slot rather than taking both and hiding its counterpart.
  forecastSeries
    .filter(({ item }) => !item.isComparisonForecast)
    .forEach(({ source }) => addSource(source, ForecastCompareLineStyle.Dashed));
  forecastSeries
    .filter(({ item }) => item.isComparisonForecast)
    .forEach(({ source }) => addSource(source, ForecastCompareLineStyle.Dotted));

  return folders.slice(0, 2);
}
